/**
 * On-demand agent catalog filters for FlexibleCRUD toolbar.
 * Toggle sits beside Search; panel expands below the toolbar when open.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import {
  AE_INPUT_COMPACT,
  AE_LABEL_COMPACT,
  AE_SELECT_COMPACT,
} from '@/components/assessment-engine/layout/aeTheme';
import { listAgents } from '@/services/aiStudioAgentService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { STUDIO_AGENT_VISIBILITIES } from '@/types/aiStudioAgent';
import type { AiStudioAgent, StudioAgentVisibility } from '@/types/aiStudioAgent';

export type AgentCatalogFilterValues = {
  visibility: StudioAgentVisibility | '';
  scope: '' | 'platform' | 'tenant';
  domain: string;
  tenantId: string;
  createdBy: string;
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
};

export const EMPTY_AGENT_CATALOG_FILTERS: AgentCatalogFilterValues = {
  visibility: '',
  scope: '',
  domain: '',
  tenantId: '',
  createdBy: '',
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: '',
};

/** Serialize for FlexibleCRUDTable.extraQueryParams (omit empty). */
export function agentFiltersToQueryParams(
  filters: AgentCatalogFilterValues
): Record<string, string | undefined> {
  return {
    visibility: filters.visibility || undefined,
    scope: filters.scope || undefined,
    domain: filters.domain || undefined,
    tenantId: filters.tenantId || undefined,
    createdBy: filters.createdBy || undefined,
    createdFrom: filters.createdFrom || undefined,
    createdTo: filters.createdTo || undefined,
    updatedFrom: filters.updatedFrom || undefined,
    updatedTo: filters.updatedTo || undefined,
  };
}

export function agentCatalogFiltersActive(filters: AgentCatalogFilterValues): boolean {
  return Object.values(filters).some((value) => value !== '');
}

function countActiveFilters(filters: AgentCatalogFilterValues): number {
  return Object.values(filters).filter((value) => value !== '').length;
}

function useAgentFilterFacets(enabled: boolean) {
  const [facetAgents, setFacetAgents] = useState<AiStudioAgent[]>([]);
  const [facetsLoaded, setFacetsLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || facetsLoaded) return;
    let cancelled = false;
    listAgents({ audience: 'factory' })
      .then((agents) => {
        if (!cancelled) {
          setFacetAgents(agents);
          setFacetsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFacetAgents([]);
          setFacetsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, facetsLoaded]);

  const domains = useMemo(() => {
    const set = new Set<string>();
    facetAgents.forEach((agent) => {
      const domain = agent.domain?.trim();
      if (domain) set.add(domain);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [facetAgents]);

  const creators = useMemo(() => {
    const map = new Map<string, string>();
    facetAgents.forEach((agent) => {
      if (!agent.createdBy) return;
      const label = agent.createdByName?.trim() || agent.createdBy;
      if (!map.has(agent.createdBy)) map.set(agent.createdBy, label);
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facetAgents]);

  const tenants = useMemo(() => {
    const map = new Map<string, string>();
    facetAgents.forEach((agent) => {
      if (!agent.tenantId) return;
      const label = agent.tenantName?.trim() || agent.tenantId;
      if (!map.has(agent.tenantId)) map.set(agent.tenantId, label);
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facetAgents]);

  return { domains, creators, tenants };
}

type ToggleProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: AgentCatalogFilterValues;
  onChange: (next: AgentCatalogFilterValues) => void;
};

/** Filter icon control for FlexibleCRUD `toolbarAfterSearch`. */
export const AgentCatalogFilterToggle: React.FC<ToggleProps> = ({
  open,
  onOpenChange,
  value,
  onChange,
}) => {
  const { t } = useLanguage();
  const activeCount = countActiveFilters(value);
  const active = activeCount > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="agent-catalog-filters-panel"
        onClick={() => onOpenChange(!open)}
        className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] ${
          open || active
            ? 'border-[var(--color-accent-bg)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
            : 'border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]'
        }`}
      >
        <FunnelIcon className="h-4 w-4" aria-hidden />
        {t('aiStudio.agents.filtersHeading')}
        {active && (
          <span className="rounded-full bg-[var(--color-surface-bg)] px-1.5 text-xs font-semibold text-[var(--color-surface-text)]">
            {activeCount}
          </span>
        )}
      </button>
      {active && (
        <button
          type="button"
          onClick={() => onChange({ ...EMPTY_AGENT_CATALOG_FILTERS })}
          className="text-xs font-medium text-[var(--color-accent-bg)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
        >
          {t('aiStudio.agents.filtersClear')}
        </button>
      )}
    </div>
  );
};

type PanelProps = {
  value: AgentCatalogFilterValues;
  onChange: (next: AgentCatalogFilterValues) => void;
};

/** Expanded filter fields for FlexibleCRUD `belowToolbar` when open. */
export const AgentCatalogFilterPanel: React.FC<PanelProps> = ({ value, onChange }) => {
  const { t } = useLanguage();
  const { domains, creators, tenants } = useAgentFilterFacets(true);

  const patch = (partial: Partial<AgentCatalogFilterValues>) => {
    onChange({ ...value, ...partial });
  };

  return (
    <div
      id="agent-catalog-filters-panel"
      className="border-t border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 pb-2 pt-2"
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-visibility">
            {t('aiStudio.agents.statusFilter')}
          </label>
          <select
            id="agent-filter-visibility"
            className={AE_SELECT_COMPACT}
            value={value.visibility}
            onChange={(e) =>
              patch({ visibility: e.target.value as StudioAgentVisibility | '' })
            }
          >
            <option value="">{t('aiStudio.agents.allStatuses')}</option>
            {STUDIO_AGENT_VISIBILITIES.map((state) => (
              <option key={state} value={state}>
                {t(`aiStudio.agents.state.${state}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-scope">
            {t('aiStudio.agents.filterScope')}
          </label>
          <select
            id="agent-filter-scope"
            className={AE_SELECT_COMPACT}
            value={value.scope}
            onChange={(e) =>
              patch({ scope: e.target.value as '' | 'platform' | 'tenant' })
            }
          >
            <option value="">{t('aiStudio.agents.filterAll')}</option>
            <option value="platform">{t('aiStudio.agents.scopePlatform')}</option>
            <option value="tenant">{t('aiStudio.agents.scopeTenant')}</option>
          </select>
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-domain">
            {t('aiStudio.agents.filterDomain')}
          </label>
          <select
            id="agent-filter-domain"
            className={AE_SELECT_COMPACT}
            value={value.domain}
            onChange={(e) => patch({ domain: e.target.value })}
          >
            <option value="">{t('aiStudio.agents.filterAll')}</option>
            {domains.map((domain) => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-tenant">
            {t('aiStudio.agents.filterTenant')}
          </label>
          <select
            id="agent-filter-tenant"
            className={AE_SELECT_COMPACT}
            value={value.tenantId}
            onChange={(e) => patch({ tenantId: e.target.value })}
          >
            <option value="">{t('aiStudio.agents.filterAll')}</option>
            <option value="__platform__">{t('aiStudio.agents.scopePlatform')}</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-created-by">
            {t('aiStudio.agents.filterCreatedBy')}
          </label>
          <select
            id="agent-filter-created-by"
            className={AE_SELECT_COMPACT}
            value={value.createdBy}
            onChange={(e) => patch({ createdBy: e.target.value })}
          >
            <option value="">{t('aiStudio.agents.filterAll')}</option>
            {creators.map((creator) => (
              <option key={creator.id} value={creator.id}>
                {creator.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-created-from">
            {t('aiStudio.agents.filterCreatedFrom')}
          </label>
          <input
            id="agent-filter-created-from"
            type="date"
            className={AE_INPUT_COMPACT}
            value={value.createdFrom}
            onChange={(e) => patch({ createdFrom: e.target.value })}
          />
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-created-to">
            {t('aiStudio.agents.filterCreatedTo')}
          </label>
          <input
            id="agent-filter-created-to"
            type="date"
            className={AE_INPUT_COMPACT}
            value={value.createdTo}
            onChange={(e) => patch({ createdTo: e.target.value })}
          />
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-updated-from">
            {t('aiStudio.agents.filterUpdatedFrom')}
          </label>
          <input
            id="agent-filter-updated-from"
            type="date"
            className={AE_INPUT_COMPACT}
            value={value.updatedFrom}
            onChange={(e) => patch({ updatedFrom: e.target.value })}
          />
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="agent-filter-updated-to">
            {t('aiStudio.agents.filterUpdatedTo')}
          </label>
          <input
            id="agent-filter-updated-to"
            type="date"
            className={AE_INPUT_COMPACT}
            value={value.updatedTo}
            onChange={(e) => patch({ updatedTo: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};
